import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Check for messages from signup redirect
  useEffect(() => {
    if (location?.state?.message) {
      setInfo(location?.state?.message);
      window.history?.replaceState(null, document.title, location?.pathname);

    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
    setError('');
    setInfo('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: signInError } = await authService?.signIn(formData?.email, formData?.password);
      
      if (signInError) {
        setError(signInError?.message || 'Error al iniciar sesión');
        setLoading(false);
        return;
      }

      if (data?.user) {
        // Wait for profile with timeout
        const profile = await authService?.waitForProfile(data?.user?.id);
        
        if (profile) {
          // Role-based redirect
          if (profile?.role === 'admin') {
            navigate('/admin-dashboard', { replace: true });
          } else {
            navigate('/user-dashboard', { replace: true });
          }
        } else {
          // Fallback if profile not found
          navigate('/user-dashboard', { replace: true });
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Error de conexión. Por favor, inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-2xl mb-3 sm:mb-4">
            <Icon name="Mountain" size={28} color="var(--color-primary)" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
            Residencial 3 Volcanes
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Inicia sesión en tu cuenta
          </p>
        </div>

        {/* Sign In Form */}
        <div className="card p-6 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error flex-1">{error}</p>
              </div>
            )}

            {info && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-primary)" />
                <p className="text-sm text-primary flex-1">{info}</p>
              </div>
            )}

            <div>
              <Input
                type="email"
                name="email"
                label="Correo Electrónico"
                placeholder="tu@email.com"
                value={formData?.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="Contraseña"
                  placeholder="••••••••"
                  value={formData?.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                  disabled={loading}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
            </div>

            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
              size="lg"
            >
              Iniciar Sesión
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-5 sm:mt-6 text-center">
            <p className="text-xs sm:text-sm text-muted-foreground">
              ¿No tienes cuenta?{' '}
              <Link to="/signup" className="text-primary hover:underline font-medium">
                Regístrate aquí
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;